import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { KeycloakService } from '../services/keycloak.service';
import { AuthApiService } from '../services/auth-api.service';
import type { UserProfile } from '../services/auth-api.service';

interface AuthContextType {
  /** Backend user profile (from /api/auth/me) */
  user: UserProfile | null;
  /** Keycloak authentication status */
  isAuthenticated: boolean;
  /** True while Keycloak is initializing */
  isLoading: boolean;
  /** True when token refresh failed — user needs to re-login */
  sessionExpired: boolean;
  /** Remaining analysis credits */
  credits: number;
  /** Redirect to Keycloak login page */
  login: () => void;
  /** Redirect to Keycloak registration page */
  register: () => void;
  /** End Keycloak session and redirect to landing */
  logout: () => void;
  /** Re-fetch user profile and credits from backend */
  refreshProfile: () => Promise<void>;
  /** Use a credit (call after analysis) */
  useCredit: (amount?: number) => Promise<void>;
  /** Redirect to Keycloak to re-authenticate (after session expired) */
  reconnect: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [credits, setCredits] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Initialize Keycloak on mount
  useEffect(() => {
    initAuth();
  }, []);

  // Listen for session expired events from KeycloakService (token refresh failed)
  useEffect(() => {
    const unsubscribe = KeycloakService.onSessionExpired(() => {
      console.warn('Session expired — showing reconnect prompt');
      setSessionExpired(true);
    });
    return unsubscribe;
  }, []);

  // Refresh token when tab returns from background/idle — only if near expiry
  useEffect(() => {
    const onVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!KeycloakService.isAuthenticated()) return;

      // Only refresh if token expires within 60s (not forced every time)
      const ok = await KeycloakService.refreshToken(60);
      if (!ok) {
        console.warn('Token refresh failed after tab resume — session expired');
        setSessionExpired(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const initAuth = async () => {
    setIsLoading(true);
    try {
      let authenticated = await KeycloakService.init();

      // If check-sso says not authenticated, try one token refresh
      // (handles case where SSO iframe timed out but refresh token is still valid)
      if (!authenticated && KeycloakService.instance.refreshToken) {
        authenticated = await KeycloakService.refreshToken();
      }

      setIsAuthenticated(authenticated);

      if (authenticated) {
        // Try to fetch profile first; only register if profile doesn't exist
        const profileOk = await fetchProfile();
        if (!profileOk) {
          await registerInBackend();
          await fetchProfile();
        }
      }
    } catch (error) {
      console.warn('Auth init error (check-sso likely timed out):', error);

      // check-sso iframe can throw on timeout / 3rd-party cookie block,
      // but a refresh token may still be valid — try it before giving up
      try {
        if (KeycloakService.instance.refreshToken) {
          const recovered = await KeycloakService.refreshToken();
          if (recovered) {
            setIsAuthenticated(true);
            await fetchProfile();
            return; // finally block still runs
          }
        }
      } catch {
        // refresh also failed — fall through
      }

      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Register user profile in backend after Keycloak login.
   * Uses Keycloak token claims for firstName/lastName.
   * 409 response means user already registered — that's fine.
   */
  const registerInBackend = async () => {
    try {
      const kcUser = KeycloakService.getUserFromToken();
      if (!kcUser) return;

      await AuthApiService.registerProfile({
        firstName: kcUser.given_name || kcUser.name || '',
        lastName: kcUser.family_name || '',
      });
    } catch (error) {
      // Non-409 errors are logged but don't block the flow
      console.warn('Backend registration:', error);
    }
  };

  /**
   * Fetch user profile from backend /api/auth/me
   */
  const fetchProfile = async (): Promise<boolean> => {
    try {
      const profile = await AuthApiService.getProfile();
      setUser(profile);
      setCredits(profile.creditsRemained ?? 0);
      return true;
    } catch {
      return false;
    }
  };

  const login = useCallback(() => {
    KeycloakService.login();
  }, []);

  const register = useCallback(() => {
    KeycloakService.register();
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
    setSessionExpired(false);
    setCredits(0);
    KeycloakService.logout();
  }, []);

  const reconnect = useCallback(() => {
    setSessionExpired(false);
    KeycloakService.login();
  }, []);

  const refreshProfile = useCallback(async () => {
    if (KeycloakService.isAuthenticated()) {
      await fetchProfile();
    }
  }, []);

  const useCredit = useCallback(async (amount: number = 1) => {
    await AuthApiService.useCredit(amount);
    // Refresh credits after use
    setCredits((prev) => Math.max(0, prev - amount));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        sessionExpired,
        credits,
        login,
        register,
        logout,
        refreshProfile,
        useCredit,
        reconnect,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
